<template>
  <div class="export-page p-2 p-md-3">
    <b-card class="export-card border-0">
      <div class="d-flex flex-column flex-lg-row align-items-lg-center justify-content-between mb-3">
        <div>
          <div class="export-kicker">Orders Export</div>
          <h3 class="mb-1">{{ pageTitle }}</h3>
          <p class="text-muted mb-0">নির্বাচিত status এবং date range অনুযায়ী CSV export করুন।</p>
        </div>

        <b-badge variant="light-primary" class="export-status-badge mt-2 mt-lg-0">
          {{ readableStatus }}
        </b-badge>
      </div>

      <div class="range-tabs-wrap mb-3">
        <button
          v-for="option in quickRangeOptions"
          :key="option.value"
          type="button"
          class="range-tab-btn"
          :class="{ active: range === option.value }"
          @click="setRange(option.value)"
        >
          {{ option.text }}
        </button>
      </div>

      <b-row v-if="range === 'custom'" class="mb-2">
        <b-col md="4">
          <label class="premium-label">From Date</label>
          <input
            v-model="fromDate"
            type="date"
            class="form-control premium-date-input"
          />
        </b-col>

        <b-col md="4">
          <label class="premium-label">To Date</label>
          <input
            v-model="toDate"
            type="date"
            class="form-control premium-date-input"
          />
        </b-col>
      </b-row>

      <div v-if="range === 'custom' && isInvalidCustomRange" class="text-danger small mb-2">
        Custom range ব্যবহার করতে From Date এবং To Date দুটোই দিন।
      </div>

      <div class="export-action-wrap">
        <button
          type="button"
          class="premium-export-btn"
          @click="downloadCSV"
          :disabled="isDownloading || isInvalidCustomRange"
        >
          <span class="btn-icon" aria-hidden="true">
            <svg viewBox="0 0 24 24" fill="none">
              <path d="M12 3V14" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
              <path d="M8 10L12 14L16 10" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M5 19H19" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
            </svg>
          </span>
          <span>{{ buttonText }}</span>
        </button>
      </div>

      <div v-if="isDownloading" class="export-progress-wrap mt-3">
        <div class="export-progress-head">
          <span>Preparing export...</span>
          <span>{{ progressValue }}%</span>
        </div>
        <div class="export-progress-bar">
          <div class="export-progress-fill" :style="{ width: progressValue + '%' }"></div>
        </div>
      </div>

      <div class="export-help mt-3">
        CSV fields: Customer Name, Mobile Number, Address, Total Amount
      </div>
    </b-card>
  </div>
</template>

<script>
import axios from '@/helpers/axios'

export default {
  data() {
    return {
      range: 'today',
      fromDate: '',
      toDate: '',
      isDownloading: false,
      progressValue: 0,
      progressTimer: null,
      quickRangeOptions: [
        { value: 'today', text: 'Today' },
        { value: 'yesterday', text: 'Yesterday' },
        { value: 'last7days', text: 'Last 7 Days' },
        { value: 'last30days', text: 'Last 30 Days' },
        { value: 'last1year', text: 'Last 1 Year' },
        { value: 'all', text: 'All' },
        { value: 'custom', text: 'Custom Range' },
      ],
    }
  },
  computed: {
    status() {
      return this.$route.meta.exportStatus || ''
    },
    pageTitle() {
      return this.$route.meta.pageTitle || 'Orders Export'
    },
    readableStatus() {
      return (this.status || '')
        .replace(/-/g, ' ')
        .replace(/_/g, ' ')
        .replace(/\b\w/g, char => char.toUpperCase())
    },
    isInvalidCustomRange() {
      return this.range === 'custom' && (!this.fromDate || !this.toDate)
    },
    buttonText() {
      return this.isDownloading ? 'Preparing CSV...' : 'Download CSV'
    },
  },
  beforeDestroy() {
    this.clearProgressTimer()
  },
  methods: {
    setRange(value) {
      this.range = value
    },
    clearProgressTimer() {
      if (this.progressTimer) {
        window.clearInterval(this.progressTimer)
        this.progressTimer = null
      }
    },
    startProgress() {
      this.progressValue = 8
      this.clearProgressTimer()

      this.progressTimer = window.setInterval(() => {
        if (this.progressValue < 90) {
          this.progressValue += 9
        } else {
          this.clearProgressTimer()
        }
      }, 180)
    },
    finishProgress() {
      this.progressValue = 100
      window.setTimeout(() => {
        this.isDownloading = false
        this.progressValue = 0
      }, 500)
    },
    downloadCSV() {
      if (!this.status) return
      if (this.isInvalidCustomRange) return

      this.isDownloading = true
      this.startProgress()

      const base = (axios.defaults.baseURL || '').replace(/\/$/, '')
      const params = new URLSearchParams({ range: this.range })

      if (this.range === 'custom') {
        params.append('from_date', this.fromDate)
        params.append('to_date', this.toDate)
      }

      const url = `${base}/api/orders/export/${this.status}?${params.toString()}`

      window.open(url, '_blank')
      this.finishProgress()
    },
  },
}
</script>

<style scoped>
.export-card {
  border-radius: 20px;
  box-shadow: 0 12px 36px rgba(34, 41, 47, 0.07);
}

.export-kicker {
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #7367f0;
  margin-bottom: 6px;
}

.export-status-badge {
  font-size: 13px;
  padding: 0.7rem 0.95rem;
  border-radius: 999px;
}

.range-tabs-wrap {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.range-tab-btn {
  border: 1px solid rgba(115, 103, 240, 0.16);
  background: #fff;
  color: #6e6b7b;
  border-radius: 999px;
  padding: 0.72rem 1rem;
  font-size: 13px;
  font-weight: 600;
  line-height: 1;
  transition: all 0.2s ease;
  box-shadow: none;
}

.range-tab-btn:hover {
  border-color: rgba(115, 103, 240, 0.35);
  color: #7367f0;
}

.range-tab-btn.active {
  background: linear-gradient(135deg, #7367f0 0%, #5f55d9 100%);
  color: #fff;
  border-color: transparent;
  box-shadow: 0 10px 24px rgba(115, 103, 240, 0.22);
}

.premium-label {
  display: block;
  margin-bottom: 0.55rem;
  font-size: 13px;
  font-weight: 600;
  color: #5e5873;
}

.premium-date-input {
  height: 46px;
  border-radius: 12px;
  border: 1px solid rgba(115, 103, 240, 0.16);
  box-shadow: none;
}

.premium-date-input:focus {
  border-color: rgba(115, 103, 240, 0.45);
  box-shadow: 0 0 0 0.2rem rgba(115, 103, 240, 0.1);
}

.export-action-wrap {
  display: flex;
  align-items: center;
  justify-content: flex-start;
}

.premium-export-btn {
  min-width: 260px;
  height: 48px;
  border: 0;
  border-radius: 14px;
  background: linear-gradient(135deg, #7367f0 0%, #5f55d9 100%);
  color: #fff;
  font-weight: 600;
  font-size: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  box-shadow: 0 12px 28px rgba(115, 103, 240, 0.28);
  transition: all 0.2s ease;
}

.premium-export-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 16px 32px rgba(115, 103, 240, 0.34);
}

.premium-export-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
}

.btn-icon {
  width: 18px;
  height: 18px;
  display: inline-flex;
}

.btn-icon svg {
  width: 18px;
  height: 18px;
}

.export-progress-wrap {
  max-width: 420px;
}

.export-progress-head {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 8px;
  font-size: 12px;
  font-weight: 600;
  color: #7367f0;
}

.export-progress-bar {
  height: 8px;
  background: #ecebff;
  border-radius: 999px;
  overflow: hidden;
}

.export-progress-fill {
  height: 100%;
  border-radius: 999px;
  background: linear-gradient(135deg, #7367f0 0%, #5f55d9 100%);
  transition: width 0.2s ease;
}

.export-help {
  font-size: 13px;
  color: #6e6b7b;
}

@media (max-width: 767px) {
  .premium-export-btn {
    width: 100%;
    min-width: 0;
  }

  .range-tabs-wrap {
    gap: 8px;
  }

  .range-tab-btn {
    font-size: 12px;
    padding: 0.68rem 0.88rem;
  }

  .export-progress-wrap {
    max-width: 100%;
  }
}
</style>